import React, { useRef, useEffect, useState } from "react";
import "@kitware/vtk.js/Rendering/OpenGL/Profiles/All";
import DataAccessHelper from "@kitware/vtk.js/IO/Core/DataAccessHelper";
import vtkFullScreenRenderWindow from "@kitware/vtk.js/Rendering/Misc/FullScreenRenderWindow";
import vtkSynchronizableRenderWindow from "@kitware/vtk.js/Rendering/Misc/SynchronizableRenderWindow";
import "@kitware/vtk.js/IO/Core/DataAccessHelper/JSZipDataAccessHelper";
import "@kitware/vtk.js/IO/Core/DataAccessHelper/HtmlDataAccessHelper";
import { ProgressBar } from "baseui/progress-bar";
import Alert from "baseui/icon/alert";
import { styled } from "baseui";
import { useStyletron } from "baseui";

const ViewerContainer = styled("div", { position: "relative", width: "100%", height: "100%" });
const VtkContainer = styled("div", { width: "100%", height: "100%" });
const ProgressWrapper = styled("div", {
  position: "absolute",
  top: "10px",
  left: "50%",
  transform: "translateX(-50%)",
  width: "80%",
  maxWidth: "400px",
  zIndex: 1,
});
const ErrorWrapper = styled("div", {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  textAlign: "center",
  zIndex: 2,
});
const ErrorText = styled("div", { marginTop: "12px", color: "#FF5630", fontSize: "16px" });

export default function VtkViewerComponent({ fileData }) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [css, theme] = useStyletron();

  // ---------- create the render window once ----------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const fullScreen = vtkFullScreenRenderWindow.newInstance({
      background: [1, 1, 1],
      rootContainer: container,
      containerStyle: { width: "100%", height: "100%" },
    });
    const renderWindow = fullScreen.getRenderWindow();
    const syncCTX = vtkSynchronizableRenderWindow.getSynchronizerContext();
    const syncRW = vtkSynchronizableRenderWindow.decorate(renderWindow);
    rendererRef.current = { syncCTX, syncRW };
    return () => renderWindow.delete();
  }, []);

  // ---------- process the file blob when it changes ----------
  useEffect(() => {
    if (!fileData) {
      setError("No file data provided.");
      return;
    }

    const processFile = async () => {
      setProgress(0);
      setError(null);

      try {
        // Convert Blob to File if needed
        const file = fileData instanceof Blob 
          ? new File([fileData], "vtkFile.vtksz", { type: fileData.type })
          : fileData;

        setProgress(100);

        // Feed the file to VTK
        const { syncCTX, syncRW } = rendererRef.current;
        if (!syncCTX || !syncRW) return;

        const onReady = (data) => {
          syncCTX.setFetchArrayFunction((hash) =>
            Promise.resolve(data.hashes[hash].content)
          );
          syncRW.synchronize(data.scene);
          syncRW.render();
        };

        const helper = DataAccessHelper.get("zip", {
          zipContent: file,
          callback: () => {
            helper.fetchJSON(null, "index.json").then(onReady);
          },
        });
      } catch (err) {
        console.error("Error processing VTK file:", err);
        setError("Failed to process the VTK file.");
      }
    };

    processFile();
  }, [fileData]);

  return (
    <ViewerContainer>
      {progress < 100 && !error && (
        <ProgressWrapper>
          <ProgressBar value={progress} successValue={100} />
        </ProgressWrapper>
      )}

      {error && (
        <ErrorWrapper>
          <Alert size="128px" color={theme.colors.negative} />
          <ErrorText>{error}</ErrorText>
        </ErrorWrapper>
      )}

      <VtkContainer ref={containerRef} />
    </ViewerContainer>
  );
}